import { jsx } from "panel";
import clamp from "lodash/clamp";
import { selx } from "clsx";
import { SvgIconEl } from "common/components/svg-icon";
import { ButtonEl } from "../button";
import { ButtonSize, ButtonTheme } from "../button/constants";
import { TooltipEl } from "../tooltip";
import { SpinnerEl } from "../spinner";
import { InputLabelEl } from "./label";
import { InputType, ValidationStatus } from "./types";
import styles from "./index.module.styl";
function RightIcon({
  cmp
}) {
  if (cmp.params.hideRightIcon) {
    return null;
  }
  const icon = {
    valid: `circle-checkmark`,
    invalid: `warning`
  }[cmp.params.validationStatus];
  if (cmp.params.validationStatus) {
    return /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputRightIcon, {
      [styles.valid]: cmp.params.validationStatus === `valid`,
      [styles.invalid]: cmp.params.validationStatus === `invalid`
    }) }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
      icon
    } }));
  } else if (cmp.params.alwaysShowClear || cmp.state.value && cmp.params.showClear) {
    return /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputRightIcon, styles.clickable), on: {
      click: (ev) => cmp.handleClearInput(ev)
    } }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
      icon: `circle-clear`
    }, sel: selx(styles.mpInputClickableIcon) }));
  } else if (cmp.params.preText && cmp.params.helpText) {
    return /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputRightIcon) }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
      icon: `info-xs`
    } }), /* @__PURE__ */ jsx(TooltipEl, { attrs: {
      text: cmp.params.helpText
    } }));
  }
  return null;
}
function NumericControls({
  cmp
}) {
  return /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputNumericArrowControls), "data-sentry-component": "NumericControls", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx(ButtonEl, { sel: selx(styles.numericArrowButton), attrs: {
    theme: ButtonTheme.Tertiary,
    size: ButtonSize.Tiny,
    icon: `caret-up`
  }, on: {
    mousedown: (ev) => {
      ev.preventDefault();
      cmp.update({
        value: String(clamp(parseInt(cmp.state.value, 10) + 1, parseInt(cmp.params.min, 10), parseInt(cmp.params.max, 10)))
      });
      cmp.debouncedChange({
        ...ev,
        target: cmp.searchInput
      });
    }
  }, "data-sentry-element": "ButtonEl", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ jsx(ButtonEl, { sel: selx(styles.numericArrowButton), attrs: {
    theme: ButtonTheme.Tertiary,
    size: ButtonSize.Tiny,
    icon: `caret-down`
  }, on: {
    mousedown: (ev) => {
      ev.preventDefault();
      cmp.update({
        value: String(clamp(parseInt(cmp.state.value, 10) - 1, parseInt(cmp.params.min, 10), parseInt(cmp.params.max, 10)))
      });
      cmp.debouncedChange({
        ...ev,
        target: cmp.searchInput
      });
    }
  }, "data-sentry-element": "ButtonEl", "data-sentry-source-file": "template.tsx" }));
}
export function Template() {
  const {
    value
  } = this.state;
  const validationStatus = this.params.validationStatus;
  const isValid = validationStatus === ValidationStatus.Valid;
  const isInvalid = validationStatus === ValidationStatus.Invalid;
  return /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputContainer, {
    [styles.themeWhite]: this.params.theme === `white`
  }), "data-sentry-component": "Template", "data-sentry-source-file": "template.tsx" }, this.params.label ? /* @__PURE__ */ jsx(InputLabelEl, { sel: selx(styles.inputLabel), attrs: {
    label: this.params.label,
    "field-id": this.params.fieldId,
    necessary: this.params.necessary,
    "help-text": this.params.helpText,
    "extra-info": this.params.extraInfo
  }, on: {
    click: () => this.params.disabled === false && this.focus()
  } }) : null, /* @__PURE__ */ jsx(
    "div",
    {
      sel: selx(styles.mpInputFieldContainer, {
        [styles.hasMinInputWidth]: Boolean(this.params.minInputWidth),
        [styles.hasMultilinePrefix]: this.params.multilinePrefix,
        [styles.hideFocusStyling]: this.params.hideFocusStyling,
        [styles.isDisabled]: this.params.disabled,
        [styles.sizeSmall]: this.params.size === `small`,
        [styles.sizeMedium]: this.params.size === `medium`,
        [styles.sizeLarge]: this.params.size === `large`,
        [styles.typeNumber]: this.params.type === `number`,
        [styles.typePassword]: this.params.type === `password`,
        [styles.typeText]: this.params.type === `text`,
        [styles.isValid]: isValid
      }),
      class: {
        [styles.isInvalid]: isInvalid
      },
      on: {
        click: () => this.focus()
      }
    },
    (() => {
      var _a, _b;
      const TagName = this.params.type === `textarea` ? `textarea` : `input`;
      return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputLeftArea) }, this.params.icon && /* @__PURE__ */ jsx("div", { sel: selx(`mp-input-left-icon`) }, this.params.icon === `spinner` ? /* @__PURE__ */ jsx(SpinnerEl, { sel: selx(styles.mpInputLeftIcon) }) : /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
        icon: this.params.icon
      }, sel: selx(styles.mpInputLeftIcon) })), this.params.preText && /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputPreText, {
        [styles.sizeSmall]: this.params.size === `small`,
        [styles.sizeMedium]: this.params.size === `medium`,
        [styles.sizeLarge]: this.params.size === `large`
      }), on: {
        click: () => this.focus()
      } }, this.params.preText)), /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputWrapper), hook: {
        insert: ({
          elm
        }) => this.handleInputWrapperInsert(elm)
      } }, this.params.prefixRenderer && (this.params.multilinePrefix ? this.params.prefixRenderer() : /* @__PURE__ */ jsx("div", { hook: {
        insert: ({
          elm
        }) => this.handleInputPrefixInsert(elm)
      } }, this.params.prefixRenderer())), /* @__PURE__ */ jsx(TagName, { sel: selx(styles.mpInput), style: {
        "text-align": this.params.textAlign,
        ...this.params.minInputWidth && {
          "min-width": `${this.params.minInputWidth}px`
        },
        ...this.params.multilinePrefix && {
          width: this.state.textWidth ? `${this.state.textWidth}px` : `auto`
        }
      }, attrs: {
        ...this.params.ariaLabel ? {
          [`aria-label`]: this.params.ariaLabel
        } : {},
        autocomplete: this.params.autocomplete,
        elref: `inputTag`,
        id: this.params.fieldId,
        name: this.params.fieldName,
        placeholder: this.params.placeholder,
        disabled: this.params.disabled,
        readonly: this.params.readonly,
        size: 1,
        // We do custom comma formatting on number type inputs.
        // Browsers won't accept something like '1,000' as a valid
        // do do not use the "type" attribute in this case.
        type: TagName === `input` && this.params.type !== InputType.Number ? this.params.type : null,
        maxlength: this.params.maxlength,
        minlength: TagName === `input` ? this.params.min : null,
        min: this.supportsMinMaxAttrs() ? this.params.min : null,
        // only enforces arrow key decrements
        max: this.supportsMinMaxAttrs() ? this.params.max : null,
        // only enforces arrow key increments
        step: this.supportsMinMaxAttrs() ? this.params.step : null,
        // only enforces arrow key increments
        "not-focusable": this.params.notFocusable
      }, props: {
        value: this.displayValue
      }, on: {
        click: (ev) => this.handleClick(ev),
        input: (ev) => this.handleInput(ev),
        blur: (ev) => this.handleBlur(ev),
        focus: (ev) => this.handleFocus(ev),
        change: (ev) => this.handleChange(ev),
        keydown: (ev) => this.handleKeydown(ev),
        keyup: (ev) => this.handleKeyup(ev),
        paste: (ev) => this.handlePaste(ev)
      }, hook: {
        insert: ({
          elm
        }) => {
          this.handleSearchInputInsert(elm);
        }
      } })), /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputRightArea) }, (_b = (_a = this.params).suffixRenderer) == null ? void 0 : _b.call(_a), this.params.actionText && (this.params.alwaysShowActionText || value) ? /* @__PURE__ */ jsx("div", { sel: selx(styles.mpInputActionText) }, this.params.actionText) : null, this.params.type === InputType.Number && this.params.showNumericInputControls && /* @__PURE__ */ jsx(NumericControls, { cmp: this }), /* @__PURE__ */ jsx(RightIcon, { cmp: this })));
    })()
  ), validationStatus === `invalid` && this.params.errorMessage ? /* @__PURE__ */ jsx("div", { attrs: {
    elref: `inputErrorMessage`
  }, sel: selx(styles.mpInputErrorMessage) }, this.params.errorMessage) : null);
}
